import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, MessageSquare, Users, Heart, MapPin, Gift, Compass, ChefHat, Pencil, Send, Loader2, UserCog, Home as HomeIcon, Mic, MicOff, Check } from "lucide-react";
import GuidedTour from "../components/GuidedTour";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Home — minimal premium homepage with in-place AI Concierge conversation.
 *
 * The homepage is the product: a cinematic luxury hero with a single AI input
 * that transforms into a conversational chat panel. No page navigation. The
 * AI gathers details one question at a time (location, date, budget, guests,
 * preferences) and creates a TREL Request Brief. The customer reviews and
 * approves before any provider matching happens.
 *
 * Flow:
 *   IDLE → CONVERSING → BRIEF_READY → (approve) → SENDING → SENT
 *
 *   IDLE          — Headline + glass input pill + 4 chips floating over hero image
 *   CONVERSING    — Pill expands into a chat thread; AI asks one question at a time
 *   BRIEF_READY   — Chat shows brief summary card with Edit / Find My Concierge CTAs
 *   SENDING       — POST /api/briefs in flight
 *   SENT          — Confirmation + route to /customer/requests/{brief_id}
 *
 * Preview Mode: the chat endpoint is whitelisted in preview_write_guard, so
 * anonymous previewers can complete the full conversation. The final POST
 * /api/briefs is blocked (returns 403 preview_blocked) which the global
 * axios interceptor catches and opens the existing Sign-up modal.
 */

// Cinematic luxury resort hero — Maldives infinity pool at sunset.
const HERO_IMAGE_URL =
  "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=2400&q=85";

const QUICK_CHIPS = [
  { label: "Plan a Trip",  icon: Compass,   prompt: "Plan a trip" },
  { label: "Reservation",  icon: ChefHat,   prompt: "I'd like a restaurant reservation" },
  { label: "Experience",   icon: Sparkles,  prompt: "Plan a special experience" },
  { label: "Surprise Me",  icon: Gift,      prompt: "Surprise me — design something unforgettable" },
];

const HOW_STEPS = [
  { icon: MessageSquare, label: "Tell TREL" },
  { icon: Users,         label: "Get Matched" },
  { icon: Sparkles,      label: "Enjoy" },
];

const PLACEHOLDERS = [
  "What can TREL handle for you?",
  "Plan my anniversary weekend…",
  "Arrange a birthday dinner in Miami…",
  "Help me plan a family trip…",
  "Book a private chef experience…",
  "Coordinate a proposal…",
  "Handle my travel details…",
];

// State machine
const PHASE = {
  IDLE: "idle",
  CONVERSING: "conversing",
  BRIEF_READY: "brief_ready",
  SENDING: "sending",
  SENT: "sent",
};

export default function Home() {
  return (
    <div className="bg-white" data-testid="trel-home">
      <GuidedTour />
      <HomeHero />
      <HowItWorks />
      <HomeFooter />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* HERO — cinematic image, headline, AI input that becomes inline chat      */
/* ──────────────────────────────────────────────────────────────────────── */
function HomeHero() {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [draft, setDraft] = useState("");
  const [messages, setMessages] = useState([]); // {role, content}
  const [briefState, setBriefState] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState("");
  const [briefId, setBriefId] = useState(null);
  // Phase 55 chat metadata — surfaced on the BriefSummaryCard once complete.
  const [confidence, setConfidence] = useState(0);
  const [captured, setCaptured] = useState([]);
  const [missing, setMissing] = useState([]);
  const [returningHint, setReturningHint] = useState(null);
  const placeholder = useRotatingPlaceholder();
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const nav = useNavigate();

  // Auto-scroll the chat thread on every new message / thinking-state change.
  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, thinking, phase]);

  const conversing = phase !== PHASE.IDLE;

  const sendTurn = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    setError("");
    setQuickReplies([]);
    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setPhase(PHASE.CONVERSING);
    setDraft("");
    setThinking(true);
    try {
      const { data } = await api.post("/ai/concierge-chat", {
        messages: nextMessages,
        brief_state: briefState,
      });
      setBriefState(data.brief_state || briefState);
      setMessages([...nextMessages, { role: "assistant", content: data.reply }]);
      setQuickReplies(data.quick_replies || []);
      setConfidence(typeof data.confidence === "number" ? data.confidence : 0);
      setCaptured(Array.isArray(data.captured) ? data.captured : []);
      setMissing(Array.isArray(data.missing) ? data.missing : []);
      // returning_hint only ships on the FIRST turn — preserve any prior one.
      if (typeof data.returning_hint === "string") setReturningHint(data.returning_hint);
      if (data.complete) {
        setPhase(PHASE.BRIEF_READY);
      }
    } catch (e) {
      setMessages([
        ...nextMessages,
        { role: "assistant", content: "Sorry — I lost connection for a moment. Mind trying again?" },
      ]);
      setError("Connection hiccup. Try again.");
    } finally {
      setThinking(false);
    }
  }, [messages, briefState]);

  const editDetails = useCallback(() => {
    // Drop user back into the conversation — they can amend any field by
    // typing a clarification ("change the budget to Premium", "make it 4 guests").
    setPhase(PHASE.CONVERSING);
    setQuickReplies([]);
    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: "Of course — what would you like to change?" },
    ]);
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const findMyConcierge = useCallback(async () => {
    if (!briefState) return;
    setPhase(PHASE.SENDING);
    setError("");
    // Map the Phase 55 chat state → existing BriefCreateIn schema.
    const BUDGET_COMFORT_MAX = { flexible: null, affordable: 500, premium: 1500, luxury: 5000 };
    const bcRaw = String(briefState.budget_comfort || "").toLowerCase();
    let budgetMax = BUDGET_COMFORT_MAX[bcRaw];
    if (budgetMax === undefined) {
      // Custom amount — pull the first integer-like substring from the user's
      // budget_comfort answer (e.g. "$2,400" → 2400).
      const m = String(briefState.budget_comfort || "").replace(/,/g, "").match(/\d+/);
      budgetMax = m ? parseInt(m[0], 10) : null;
    }
    const peopleStr = String(briefState.people || "");
    const peopleMatch = peopleStr.replace(/,/g, "").match(/\d+/);
    const guests = peopleMatch ? Math.max(1, parseInt(peopleMatch[0], 10)) : 2;
    // Detect ISO-style explicit dates ("2026-03-14") so the backend can pin
    // them; otherwise mark the request as date-flexible.
    const tlStr = String(briefState.timeline || "");
    const isIsoDate = /^\d{4}-\d{2}-\d{2}$/.test(tlStr);
    const payload = {
      request: messages.find((m) => m.role === "user")?.content
        || briefState.goal
        || "TREL Concierge request",
      city: briefState.location || "United States",
      date: isIsoDate ? tlStr : null,
      flexible_date: !isIsoDate,
      budget_max: budgetMax,
      guests,
      category: (briefState.request_type || "events").toString().toLowerCase(),
      style: briefState.style || briefState.occasion || null,
      interests: [briefState.style, briefState.occasion]
        .filter((v) => typeof v === "string" && v.trim())
        .map((v) => v.trim()),
      important_details: [briefState.special_details, ...messages.filter((m) => m.role === "user").map((m) => m.content)]
        .filter(Boolean).join(" · "),
      ai_summary: [
        briefState.style,
        briefState.occasion,
        briefState.location && `in ${briefState.location}`,
        briefState.timeline,
        briefState.budget_comfort && `budget: ${briefState.budget_comfort}`,
      ].filter(Boolean).join(" · "),
      provider_instructions: briefState.goal || "Handle every detail end-to-end.",
      allow_remote: true,
    };
    try {
      const { data } = await api.post("/briefs", payload);
      setBriefId(data.brief_id);
      setPhase(PHASE.SENT);
      // Brief route to the customer's brief detail page if they're a real
      // logged-in customer. Preview users won't reach this branch (their
      // POST gets 403 and the global signup modal opens automatically).
      if (user?.role === "customer" && !user?.preview && data.brief_id) {
        setTimeout(() => nav(`/customer/requests/${data.brief_id}`), 2200);
      }
    } catch (e) {
      // 403 preview_blocked is handled globally by the axios interceptor
      // which opens the Sign-up modal — we just reset the UI here.
      if (e?.response?.status === 403 && e?.response?.data?.code === "preview_blocked") {
        setPhase(PHASE.BRIEF_READY);
        return;
      }
      if (e?.response?.status === 401) {
        // Anonymous visitor — route them to signup with a return-to hint.
        nav("/signup?after=brief");
        return;
      }
      setError("We couldn't send your request just yet. Please try again.");
      setPhase(PHASE.BRIEF_READY);
    }
  }, [briefState, messages, user, nav]);

  const onSubmit = (e) => {
    e?.preventDefault();
    if (phase === PHASE.BRIEF_READY || phase === PHASE.SENDING || phase === PHASE.SENT) return;
    sendTurn(draft);
  };

  return (
    <section
      className="relative isolate"
      data-testid="home-hero"
      style={{ minHeight: "min(92vh, 980px)" }}
    >
      {/* Background image with multi-layer ivory wash for legibility */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE_URL}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
        />
        {/* Stronger ivory wash so the headline + chat read on bright water.
            Two stacked layers: a flat ivory overlay (40%) for global lift,
            plus a centered radial that brightens behind the text column. */}
        <div className="absolute inset-0 bg-[#FFF8EE]/55" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 50% 48%, rgba(255,248,238,0.78) 0%, rgba(255,248,238,0.55) 45%, rgba(255,248,238,0.15) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white" />
      </div>

      <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 lg:pt-32 lg:pb-32 min-h-[88vh] flex flex-col justify-center items-center text-center">
        <h1
          data-testid="home-headline"
          className={`font-serif text-[#071A2D] leading-[1.02] transition-all duration-500 ${conversing ? "text-4xl sm:text-5xl lg:text-6xl mb-5" : "text-[44px] sm:text-7xl lg:text-[88px] lg:leading-[1.0] mb-5 sm:mb-6"}`}
          style={{ textShadow: "0 1px 2px rgba(255,248,238,0.85), 0 2px 18px rgba(255,248,238,0.6)" }}
        >
          Concierge <span className="italic text-[#064E3B]">for everyone.</span>
        </h1>

        {!conversing && (
          <p
            data-testid="home-subtext"
            className="text-[15.5px] sm:text-[17px] text-[#071A2D]/75 max-w-xl mb-10 sm:mb-12 leading-relaxed"
            style={{ textShadow: "0 1px 1px rgba(255,248,238,0.7)" }}
          >
            Trusted concierges handle the planning, booking, and details.
          </p>
        )}

        {/* Conversation thread (shown only after first message) */}
        {conversing && (
          <ChatThread
            messages={messages}
            thinking={thinking}
            threadRef={threadRef}
            briefState={briefState}
            phase={phase}
            onEdit={editDetails}
            onFind={findMyConcierge}
            briefId={briefId}
            confidence={confidence}
            captured={captured}
            missing={missing}
            returningHint={returningHint}
          />
        )}

        {/* AI input pill (always visible until SENT) */}
        {phase !== PHASE.SENT && phase !== PHASE.BRIEF_READY && (
          <ChatInput
            value={draft}
            setValue={setDraft}
            onSubmit={onSubmit}
            inputRef={inputRef}
            thinking={thinking}
            disabled={phase === PHASE.SENDING}
            placeholder={conversing ? "Tell TREL more…" : placeholder}
          />
        )}

        {/* Quick replies (idle = top-level chips; conversing = AI suggestions) */}
        {phase === PHASE.IDLE && (
          <>
            <div
              data-testid="home-chips"
              className="mt-5 sm:mt-6 flex gap-2.5 sm:gap-3 -mx-6 px-6 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible sm:flex-wrap pb-2 sm:pb-0 no-scrollbar justify-start sm:justify-center w-full max-w-2xl"
            >
              {QUICK_CHIPS.map(({ label, icon: Icon, prompt }) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`home-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => sendTurn(prompt)}
                  className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur hover:bg-white border border-stone-200 hover:border-[#C9A45C]/60 px-4 py-2.5 text-[13.5px] text-[#071A2D] shadow-[0_8px_20px_-12px_rgba(7,26,45,0.25)] transition"
                >
                  <Icon size={14} className="text-[#C9A45C]" />
                  {label}
                </button>
              ))}
            </div>

            {/* Professional entry pills — smaller, emerald-filled, secondary.
                Routes concierges + hosts into their onboarding flows without
                creating a marketing section that competes with the AI input
                (Phase 55.1). */}
            <div
              data-testid="home-pro-pills"
              className="mt-3 sm:mt-4 flex gap-2 sm:gap-2.5 -mx-6 px-6 sm:mx-0 sm:px-0 overflow-x-auto sm:overflow-visible sm:flex-wrap pb-1 sm:pb-0 no-scrollbar justify-start sm:justify-center w-full max-w-2xl"
            >
              <Link
                to="/become-provider"
                data-testid="home-pro-concierge"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#007C72] hover:bg-[#006A61] active:scale-[0.97] text-white px-3.5 py-1.5 text-[12px] font-semibold shadow-[0_6px_14px_-8px_rgba(0,124,114,0.65)] hover:shadow-[0_10px_22px_-10px_rgba(0,124,114,0.8)] hover:-translate-y-px transition-all duration-200 cursor-pointer"
              >
                Become a Concierge <ArrowRight size={12} strokeWidth={2.5} />
              </Link>
              <Link
                to="/become-host"
                data-testid="home-pro-host"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#007C72] hover:bg-[#006A61] active:scale-[0.97] text-white px-3.5 py-1.5 text-[12px] font-semibold shadow-[0_6px_14px_-8px_rgba(0,124,114,0.65)] hover:shadow-[0_10px_22px_-10px_rgba(0,124,114,0.8)] hover:-translate-y-px transition-all duration-200 cursor-pointer"
              >
                Become a Host <ArrowRight size={12} strokeWidth={2.5} />
              </Link>
            </div>
          </>
        )}

        {phase === PHASE.CONVERSING && quickReplies.length > 0 && (
          <div
            data-testid="home-quick-replies"
            className="mt-3 flex gap-2 flex-wrap justify-center w-full max-w-2xl"
          >
            {quickReplies.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`home-qr-${q.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => sendTurn(q)}
                disabled={thinking}
                className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur border border-[#C9A45C]/40 hover:border-[#C9A45C] hover:bg-[#FFF8EE] px-3.5 py-2 text-[13px] text-[#071A2D] shadow-[0_6px_16px_-10px_rgba(7,26,45,0.2)] transition disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-3 text-[12.5px] text-[#B45309]" data-testid="home-error">{error}</div>
        )}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* ChatInput — luxury floating pill                                         */
/* ──────────────────────────────────────────────────────────────────────── */
function ChatInput({ value, setValue, onSubmit, inputRef, thinking, disabled, placeholder }) {
  const { supported: micSupported, listening, toggle: toggleMic, error: micError } = useDictation((text) => setValue(text));

  return (
    <>
      <form
        onSubmit={onSubmit}
        data-testid="home-ai-form"
        className="w-full max-w-2xl relative rounded-full bg-white/95 backdrop-blur shadow-[0_22px_60px_-22px_rgba(7,26,45,0.35)] border border-stone-200 flex items-center pl-5 pr-2 py-2"
      >
        <Sparkles size={18} className="text-[#C9A45C] shrink-0" />
        <input
          ref={inputRef}
          data-testid="home-ai-input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent border-0 outline-none px-3 py-3 text-[15px] sm:text-base text-[#071A2D] placeholder-[#071A2D]/45 disabled:opacity-60"
          autoComplete="off"
        />
        {micSupported && (
          <button
            type="button"
            onClick={toggleMic}
            data-testid="home-ai-mic"
            aria-label={listening ? "Stop dictation" : "Tap to dictate"}
            aria-pressed={listening}
            disabled={disabled}
            title={listening ? "Listening…" : "Tap to dictate"}
            className={`mr-1 shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition disabled:opacity-50
                        ${listening
                          ? "bg-[#FF7A45] text-white animate-pulse shadow-[0_8px_20px_-6px_rgba(255,122,69,0.65)]"
                          : "bg-[#FFF8EE] text-[#FF7A45] hover:bg-[#FF7A45]/10 hover:text-[#FF7A45] border border-[#FF7A45]/30"}`}
          >
            {listening ? <MicOff size={17} strokeWidth={2.2} /> : <Mic size={17} strokeWidth={2.2} />}
          </button>
        )}
        <button
          type="submit"
          data-testid="home-ai-submit"
          aria-label="Send to TREL Concierge"
          disabled={disabled || thinking}
          className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#064E3B] text-white hover:brightness-110 active:scale-[0.98] transition flex items-center justify-center disabled:opacity-60"
        >
          {thinking ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
        </button>
      </form>
      {/* Quiet "Tap to dictate" hint shown only when mic is supported and we
          haven't started dictating yet. Disappears once listening or once the
          user has typed anything — to keep the hero calm. */}
      {micSupported && !listening && !value && (
        <div data-testid="home-ai-mic-hint" className="mt-2.5 text-[11px] tracking-[0.18em] uppercase text-[#FF7A45]/85 font-semibold">
          Tap to dictate
        </div>
      )}
      {micSupported && listening && (
        <div data-testid="home-ai-mic-listening" className="mt-2.5 text-[11px] tracking-[0.18em] uppercase text-[#FF7A45] font-semibold">
          Listening…
        </div>
      )}
      {micError && (
        <div data-testid="home-ai-mic-error" role="alert" className="mt-2 text-[12px] text-[#B45309] max-w-md">
          {micError}
        </div>
      )}
    </>
  );
}

/**
 * useRotatingPlaceholder — cycles through PLACEHOLDERS every ~3.4s so the
 * hero pill quietly suggests fresh request ideas without distracting the
 * eye. Pauses when the user has typed anything (parent passes its own
 * placeholder once a draft exists, so this hook is read-only on idle).
 */
function useRotatingPlaceholder() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % PLACEHOLDERS.length),
      3400
    );
    return () => clearInterval(t);
  }, []);
  return PLACEHOLDERS[idx];
}

/**
 * useDictation — minimal Web Speech API hook shared by the hero input pill.
 *
 * Mirrors the behaviour of the standalone <DictateMicButton/> (still used on
 * legacy pages) but inlines into the hero pill so we get a clean luxury
 * one-row layout. Returns `null` features on unsupported browsers so the
 * mic icon simply doesn't render — no visual debt, no errors.
 */
function useDictation(onText) {
  // Lazy-initialize from the global to avoid the react-hooks/set-state-in-effect
  // lint rule. Web Speech support is a constant for the lifetime of the page.
  const [supported] = useState(() => {
    if (typeof window === "undefined") return false;
    return Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
  });
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const finalRef = useRef("");

  // Ensure recognition is torn down on unmount so the mic indicator clears.
  useEffect(() => {
    return () => { try { recRef.current?.stop(); } catch (_) { /* idempotent */ } };
  }, []);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    finalRef.current = "";
    rec.onresult = (evt) => {
      let interim = "", final = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const r = evt.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) finalRef.current = (finalRef.current + " " + final).trim();
      onText?.((finalRef.current + " " + interim).trim());
    };
    rec.onerror = (evt) => {
      const code = evt?.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError("Microphone access blocked. Enable it in your browser settings to dictate.");
      } else if (code !== "no-speech" && code !== "aborted") {
        setError("Couldn't hear you — try again?");
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);
    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
      setError("");
    } catch (_) {
      setListening(false);
    }
  }, [onText]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch (_) { /* idempotent */ }
  }, []);

  const toggle = useCallback(() => { listening ? stop() : start(); }, [listening, start, stop]);

  return { supported, listening, toggle, error };
}

/* ──────────────────────────────────────────────────────────────────────── */
/* ChatThread — turn-by-turn bubbles + brief summary card                   */
/* ──────────────────────────────────────────────────────────────────────── */
function ChatThread({ messages, thinking, threadRef, briefState, phase, onEdit, onFind, briefId, confidence, captured, missing, returningHint }) {
  return (
    <div
      ref={threadRef}
      data-testid="home-chat-thread"
      className="w-full max-w-2xl mb-5 max-h-[48vh] sm:max-h-[52vh] overflow-y-auto rounded-3xl bg-white/85 backdrop-blur-md border border-stone-200 shadow-[0_22px_60px_-22px_rgba(7,26,45,0.25)] px-4 sm:px-5 py-4 sm:py-5 space-y-3"
    >
      {/* Returning-customer memory — shown above the first AI reply when the
          backend recognises a signed-in customer with prior briefs. */}
      {returningHint && (
        <div data-testid="home-returning-hint" className="rounded-2xl border border-[#C9A45C]/40 bg-[#FFF8EE] px-4 py-3 text-[13px] text-[#071A2D]/85 leading-snug">
          <span className="font-semibold text-[#064E3B]">Welcome back. </span>{returningHint}
        </div>
      )}
      {messages.map((m, i) => (
        <Bubble key={i} role={m.role} text={m.content} />
      ))}
      {thinking && (
        <Bubble role="assistant" text={<TypingDots />} />
      )}
      {phase === PHASE.BRIEF_READY && briefState && (
        <BriefSummaryCard
          brief={briefState}
          onEdit={onEdit}
          onFind={onFind}
          confidence={confidence}
          captured={captured}
          missing={missing}
        />
      )}
      {phase === PHASE.SENDING && <ConciergeFindingTracker stage="created" />}
      {phase === PHASE.SENT && <ConciergeFindingTracker stage="reviewing" briefId={briefId} />}
    </div>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={isUser ? "home-bubble-user" : "home-bubble-ai"}
    >
      <div
        className={`max-w-[85%] sm:max-w-[80%] px-4 py-2.5 text-[14.5px] leading-snug rounded-2xl ${
          isUser
            ? "bg-[#064E3B] text-white rounded-br-md"
            : "bg-white text-[#071A2D] border border-stone-200 rounded-bl-md"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1 items-center" aria-label="Thinking">
      <span className="w-1.5 h-1.5 bg-[#071A2D]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-[#071A2D]/40 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="w-1.5 h-1.5 bg-[#071A2D]/40 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* BriefSummaryCard — review + approve (Phase 55 schema)                    */
/* ──────────────────────────────────────────────────────────────────────── */
function BriefSummaryCard({ brief, onEdit, onFind, confidence = 95, captured = [], missing = [] }) {
  // Rows in the exact order requested by the spec: Goal · Priorities ·
  // Location · Date · People · Budget Comfort · (optional Important Details).
  const priorities = [brief.style, brief.occasion].filter(Boolean);
  const rows = [
    { key: "goal",            label: "Goal",           value: brief.goal,            icon: Sparkles },
    { key: "priorities",      label: "Priorities",     value: priorities.length ? priorities.join(" · ") : null, icon: Heart },
    { key: "location",        label: "Location",       value: brief.location,        icon: MapPin },
    { key: "timeline",        label: "Date",           value: brief.timeline },
    { key: "people",          label: "People",         value: brief.people },
    { key: "budget_comfort",  label: "Budget comfort", value: brief.budget_comfort },
    { key: "special_details", label: "Important details", value: brief.special_details },
  ].filter((r) => r.value);

  return (
    <div
      data-testid="home-brief-summary"
      data-confidence={confidence}
      className="rounded-2xl bg-[#FFF8EE] border border-[#C9A45C]/35 p-4 sm:p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#064E3B] font-semibold">
          <Sparkles size={13} className="text-[#C9A45C]" />
          Your request is ready
        </div>
        <span data-testid="brief-confidence" className="text-[11px] font-semibold text-[#064E3B] tabular-nums">
          {confidence}% complete
        </span>
      </div>

      {/* Confidence bar */}
      <div className="mt-2 h-1.5 rounded-full bg-white/80 overflow-hidden">
        <div
          className="h-full bg-[#064E3B] transition-all duration-500"
          style={{ width: `${Math.min(100, Math.max(0, confidence))}%` }}
          aria-hidden
        />
      </div>

      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-start gap-2" data-testid={`brief-row-${r.key}`}>
            <dt className="text-[11px] uppercase tracking-wider text-[#071A2D]/55 w-[108px] shrink-0 mt-0.5">
              {r.label}
            </dt>
            <dd className="text-[14px] text-[#071A2D] font-medium">{r.value}</dd>
          </div>
        ))}
      </dl>

      {/* Captured + Missing audit (only renders if we have either list) */}
      {(captured.length > 0 || missing.length > 0) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]" data-testid="brief-audit">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#064E3B] font-semibold mb-1">Captured</div>
            <ul className="space-y-0.5">
              {captured.map((c) => (
                <li key={c} className="flex items-center gap-1.5 text-[#071A2D]/80">
                  <Check size={12} strokeWidth={2.5} className="text-[#064E3B]"/> {c}
                </li>
              ))}
            </ul>
          </div>
          {missing.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#C9A45C] font-semibold mb-1">Missing</div>
              <ul className="space-y-0.5">
                {missing.map((m) => (
                  <li key={m} className="text-[#071A2D]/55">{m}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
        <button
          type="button"
          onClick={onEdit}
          data-testid="brief-edit-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#071A2D] border border-stone-300 hover:border-[#071A2D]/40 px-5 py-2.5 text-[13.5px] transition"
        >
          <Pencil size={14} /> Edit details
        </button>
        <button
          type="button"
          onClick={onFind}
          data-testid="brief-find-btn"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#064E3B] text-white hover:brightness-110 px-5 py-2.5 text-[13.5px] font-medium shadow-[0_10px_22px_-12px_rgba(6,78,59,0.7)] transition"
        >
          Find My Concierge <Send size={14} />
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* ConciergeFindingTracker — 5-stage post-submit timeline (Phase 55)        */
/* ──────────────────────────────────────────────────────────────────────── */
const FINDING_STAGES = [
  { key: "created",   label: "Request Created" },
  { key: "reviewing", label: "Concierges Reviewing" },
  { key: "received",  label: "Responses Received" },
  { key: "choose",    label: "Choose Concierge" },
  { key: "relax",     label: "Relax — Your Concierge Handles It" },
];

function ConciergeFindingTracker({ stage, briefId }) {
  const currentIdx = Math.max(0, FINDING_STAGES.findIndex((s) => s.key === stage));
  const subtitle = briefId
    ? "Opening your request room…"
    : "Your request has been shared with trusted concierges who match your needs.";
  return (
    <div
      data-testid="home-finding-tracker"
      data-stage={stage}
      className="rounded-2xl bg-[#064E3B] text-white px-5 py-5 shadow-[0_18px_40px_-18px_rgba(6,78,59,0.55)]"
    >
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[#C9A45C]" />
        <span className="text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#C9A45C]">
          Finding your concierge
        </span>
      </div>
      <div className="mt-2 text-[15px] font-serif">{FINDING_STAGES[currentIdx].label}</div>
      <div className="text-[12.5px] text-white/75 mt-1 leading-relaxed">{subtitle}</div>
      <ol className="mt-4 space-y-1.5 text-[12.5px]">
        {FINDING_STAGES.map((s, i) => {
          const done = i < currentIdx;
          const cur  = i === currentIdx;
          return (
            <li
              key={s.key}
              data-testid={`finding-stage-${s.key}`}
              className={`flex items-center gap-2 ${done ? "text-white/80" : cur ? "text-white" : "text-white/45"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${cur ? "bg-[#FF7A45] animate-pulse" : done ? "bg-[#C9A45C]" : "bg-white/30"}`} />
              <span className={cur ? "font-semibold" : ""}>{s.label}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* HOW IT WORKS — 3 icons, no paragraphs                                    */
/* ──────────────────────────────────────────────────────────────────────── */
function HowItWorks() {
  return (
    <section className="bg-white py-20 sm:py-24" data-testid="home-how">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#C9A45C]">
          How It Works
        </div>

        <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-10 items-start relative">
          <div aria-hidden className="hidden sm:block absolute top-9 left-[18%] right-[18%] border-t border-dashed border-[#C9A45C]/40" />

          {HOW_STEPS.map(({ icon: Icon, label }, i) => (
            <div key={label} className="flex flex-col items-center relative z-10" data-testid={`how-step-${i + 1}`}>
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#FFF8EE] border border-[#C9A45C]/25 flex items-center justify-center shadow-[0_8px_18px_-8px_rgba(201,164,92,0.35)]">
                <Icon size={26} className="text-[#064E3B]" strokeWidth={1.6} />
              </div>
              <div className="mt-4 text-[#071A2D] text-[14px] sm:text-base font-medium">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────────── */
/* FOOTER — tiny + utilitarian                                              */
/* ──────────────────────────────────────────────────────────────────────── */
function HomeFooter() {
  return (
    <footer className="bg-white border-t border-stone-200/70 py-10">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-[12px] text-stone-500">© {new Date().getFullYear()} TREL. Travel. Reservations. Experiences. Lifestyle.</div>
        <div className="flex items-center gap-5 text-[12.5px] text-stone-600">
          <Link to="/how" className="hover:text-stone-900">How It Works</Link>
          <Link to="/about" className="hover:text-stone-900">About</Link>
          <Link to="/become-provider" className="hover:text-stone-900">Concierge</Link>
          <Link to="/become-host" className="hover:text-stone-900">Host</Link>
        </div>
      </div>
    </footer>
  );
}
